import { useMemo } from 'react'
import type { SearchHit } from '@olmt/shared'
import { chatTitle, messagePreview } from '../lib/chat'
import { groupHits, searchSummary, SEARCH_MAX_LIMIT } from '../lib/search'
import { isolateAuto } from '../lib/bidi'
import { formatListStamp } from '../lib/time'
import { useT } from '../lib/i18n'
import { useAuthStore } from '../stores/authStore'
import { useSearchStore } from '../stores/searchStore'
import { Spinner } from './Spinner'

type SearchResultsProps = {
  /** What choosing a hit does: the list asks for a jump and opens the chat, the thread asks for one. */
  onSelect: (hit: SearchHit) => void
}

/**
 * A page of results, in the design's two groups (W3). The split, the header line and the empty case
 * are `lib/search.ts`'s decisions — this component only paints them, so the chat list's results and
 * the thread's cannot drift apart.
 *
 * The scroller is this component's own: results replace the body of whichever pane shows them, and
 * each pane has exactly one scroller at a time.
 */
export function SearchResults({ onSelect }: SearchResultsProps) {
  const t = useT()
  const query = useSearchStore((state) => state.query)
  const hits = useSearchStore((state) => state.hits)
  const limit = useSearchStore((state) => state.limit)
  const loading = useSearchStore((state) => state.loading)
  const error = useSearchStore((state) => state.error)
  const hasMore = useSearchStore((state) => state.hasMore)
  const loadMore = useSearchStore((state) => state.loadMore)

  const groups = useMemo(() => groupHits(hits), [hits])
  const summary = searchSummary(query, hits, hasMore)
  // A first page that has not arrived: a spinner. A *wider* page in flight keeps the rows it is
  // widening, and the More control below them carries the spinner instead.
  const waiting = loading && hits.length === 0
  const showEmpty = !loading && !error && query !== '' && summary === null

  return (
    <div className="chat-scroll">
      <div className="room-column px-3 py-3">
        {error && (
          <p
            role="alert"
            className="mb-2 rounded-panel bg-danger/10 px-3 py-2 text-meta text-danger ring-1 ring-danger/25 ring-inset"
          >
            {t('search.failed')}
          </p>
        )}

        {waiting && (
          <div className="flex justify-center py-8 text-muted">
            <Spinner label={t('search.loading')} />
          </div>
        )}

        {showEmpty && (
          <p className="px-1 py-8 text-center text-ui text-muted">
            {/* The reader's own words inside a chrome sentence: isolated, so an Arabic query cannot
                reorder the English one around it — and the reverse in the Arabic chrome. */}
            {t('search.empty', { query: isolateAuto(query) })}
          </p>
        )}

        {summary !== null && (
          <>
            <p role="status" className="px-1 pb-1 text-meta text-muted">
              {t(summary.key, summary.vars)}
            </p>

            {/* A heading only over a group that has rows: an empty section is furniture. */}
            {groups.messages.length > 0 && (
              <>
                <h2 className="px-1 pt-3 pb-1 text-meta font-semibold text-muted">{t('search.messages')}</h2>
                <ul>
                  {groups.messages.map((hit) => (
                    <ResultRow key={hit.message.id} hit={hit} onSelect={onSelect} />
                  ))}
                </ul>
              </>
            )}

            {groups.media.length > 0 && (
              <>
                <h2 className="px-1 pt-3 pb-1 text-meta font-semibold text-muted">{t('search.media')}</h2>
                <ul>
                  {groups.media.map((hit) => (
                    <ResultRow key={hit.message.id} hit={hit} onSelect={onSelect} />
                  ))}
                </ul>
              </>
            )}

            {/* The api has no search cursor: "More" re-asks with a wider `limit`, and the ceiling is
                the api's own. At the ceiling the control is gone rather than a button that cannot
                fetch anything — the header line above already says the page is not the whole answer. */}
            {hasMore && limit < SEARCH_MAX_LIMIT && (
              <div className="flex justify-center py-3">
                <button
                  type="button"
                  onClick={() => void loadMore()}
                  disabled={loading}
                  className="inline-flex min-h-11 items-center rounded-full px-4 text-ui text-muted ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2 hover:text-ink disabled:opacity-40"
                >
                  {loading ? <Spinner size={16} label={t('spinner.loading')} /> : t('search.more')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * One hit: the chat it lives in, what was said, and when. Both user strings are `<bdi>`-wrapped —
 * a row that carries chrome keeps the layout's alignment, so an Arabic body starts where the chrome
 * put it instead of drifting to the far end (docs/DESIGN.md §7.9 rule 2). The timestamp is the chat
 * list's own compact stamp, isolated by its formatter.
 */
function ResultRow({ hit, onSelect }: { hit: SearchHit; onSelect: (hit: SearchHit) => void }) {
  const meId = useAuthStore((state) => state.user?.id ?? null)
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(hit)}
        className="flex w-full flex-col gap-0.5 rounded-panel px-3 py-2.5 text-start transition-colors duration-quick hover:bg-surface2/60"
      >
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-body font-semibold text-ink">
            <bdi>{chatTitle(hit.chat, meId)}</bdi>
          </span>
          <span className="numeric shrink-0 text-meta text-muted">{formatListStamp(hit.message.createdAt)}</span>
        </span>
        <span className="truncate text-ui text-muted">
          <bdi>{messagePreview(hit.message, meId)}</bdi>
        </span>
      </button>
    </li>
  )
}
